import AppCard from '@nxsys/components/AppCard';
import AppLoader from '@nxsys/components/AppLoader';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  PROSPECT_DEFAULT_PAGE_SIZE,
  PROSPECT_TABLE_SCROLL,
  PROSPECT_TYPE,
} from '@nxsys/constants/prospect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { prospectActions } from '@nxsys/store/slices/prospect/reducer/prospectSlice';
import {
  Candidate,
  GetCandidateListParams,
} from '@nxsys/store/slices/prospect/types';
import { showToast } from '@nxsys/utils/toast';
import { Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionsHeader } from '../ActionsHeader';
import { getColumns } from '../columns';
import ViewCandidateModal from './ViewCandidateModal';

const CandidateTab = ({
  renderAddNewButton,
  onProgressedToOnboarding,
}: {
  renderAddNewButton: (variant: string) => React.ReactNode;
  onProgressedToOnboarding?: () => void;
}) => {
  const { messages } = useTypedIntl();
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  const isIreland = payrollCountry === IRELAND;

  const dispatch = useDispatch<AppDispatch>();
  const { candidateList } = useSelector(
    (state: AppState) => state.prospectStore,
  );
  const { loading, list, pagination, queryParams } = candidateList;

  const [selectedRows, setSelectedRows] = useState<Candidate[]>([]);

  const [viewCandidateModal, setViewCandidateModal] = useState<{
    open: boolean;
    data: Candidate | null;
  }>({
    open: false,
    data: null,
  });
  const toggleViewCandidateModal = () =>
    setViewCandidateModal({
      ...viewCandidateModal,
      open: !viewCandidateModal.open,
    });

  const [isProgressOnboarding, setIsProgressOnboarding] = useState(false);
  const [isProgressRegistration, setIsProgressRegistration] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: PROSPECT_DEFAULT_PAGE_SIZE,
    });
  }, []);

  // FE-BUG-001: Use functional setState to avoid stale closure on selectedRows
  useEffect(() => {
    setSelectedRows((prev) => {
      const newSelectedRow: Candidate[] = [];
      prev.forEach((item) => {
        const index = list.findIndex((i) => i.id === item.id);
        if (index !== -1) {
          newSelectedRow.push(list[index]);
        }
      });
      return newSelectedRow;
    });
  }, [list]);

  const handleFetchData = (params: GetCandidateListParams) => {
    dispatch(prospectActions.getCandidateList(params));
  };

  const refetchData = () => handleFetchData({ ...queryParams });

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  // The BE only sorts when it gets both halves, so they are cleared together
  const onChangeTable = (sorter?: {
    field: string;
    order: 'desc' | 'asc';
  }) => {
    handleFetchData({
      ...queryParams,
      page: 0,
      sortField: sorter?.field || undefined,
      sortType: sorter?.order || undefined,
    });
  };

  const handleViewCandidate = (data: Candidate) => {
    setViewCandidateModal({
      open: true,
      data,
    });
  };

  // FE-BUG-002: Wrap in try/finally to always reset loading state
  const handleProgressOnboardingByIds = async (ids: string[]) => {
    setIsProgressOnboarding(true);
    try {
      const params = { ids };
      await dispatch(prospectActions.progressOnboardingByIds(params)).unwrap();
      refetchData();
      // These candidates just became pending prospects — refresh the badge on
      // the Onboarding tab and the sidebar rather than guessing the new total
      dispatch(prospectActions.getProspectPendingCount());
      // The candidates now live under Onboarding, so follow them there
      onProgressedToOnboarding?.();
    } catch {
      showToast({
        message: messages['common.errorOccurred'] ?? 'An error occurred',
        type: 'error',
      });
    } finally {
      setIsProgressOnboarding(false);
    }
  };

  const handleProgressRegistrationByIds = async (ids: string[]) => {
    setIsProgressRegistration(true);
    try {
      const params = { ids };
      await dispatch(
        prospectActions.progressRegistrationByIds(params),
      ).unwrap();
      refetchData();
    } catch {
      showToast({
        message: messages['common.errorOccurred'] ?? 'An error occurred',
        type: 'error',
      });
    } finally {
      setIsProgressRegistration(false);
    }
  };

  // FE-BUG-004: Add confirmation dialog before delete
  const handleDeleteCandidateByIds = async (ids: string[]) => {
    Modal.confirm({
      title: messages['common.confirm'] || 'Confirm',
      content: `Are you sure you want to delete ${ids.length} candidate(s)?`,
      onOk: async () => {
        setIsDeleting(true);
        try {
          const params = { ids };
          await dispatch(prospectActions.deleteCandidateByIds(params)).unwrap();
          refetchData();
        } catch {
          showToast({
            message: messages['common.errorOccurred'] ?? 'An error occurred',
            type: 'error',
          });
        } finally {
          setIsDeleting(false);
        }
      },
    });
  };

  const handleProgressOnboardingSelected = () => {
    if (!selectedRows || selectedRows.length === 0) {
      showToast({
        message: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }
    handleProgressOnboardingByIds(selectedRows.map((row) => row.id));
  };
  const handleProgressRegistrationSelected = () => {
    if (!selectedRows || selectedRows.length === 0) {
      showToast({
        message: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }
    handleProgressRegistrationByIds(selectedRows.map((row) => row.id));
  };
  const handleDeleteCandidateSelected = () => {
    if (!selectedRows || selectedRows.length === 0) {
      showToast({
        message: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }
    handleDeleteCandidateByIds(selectedRows.map((row) => row.id));
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.companyIds?.length ||
    !!queryParams?.agencyIds?.length ||
    !!queryParams?.paymentModel;

  return (
    <AppCard>
      {(isProgressOnboarding || isProgressRegistration || isDeleting) && (
        <AppLoader />
      )}
      <AppsHeader>
        <ActionsHeader
          fetchData={handleFetchData}
          type={PROSPECT_TYPE.CANDIDATE}
          onProgressOnboardingSelected={handleProgressOnboardingSelected}
          onProgressRegistrationSelected={handleProgressRegistrationSelected}
          onDeleteCandidateSelected={handleDeleteCandidateSelected}
          onFilter={handleFetchData}
        />
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={PROSPECT_TABLE_SCROLL}
          titleEmpty={messages['prospect.candidate.empty']}
          columns={getColumns({
            messages,
            prospectType: PROSPECT_TYPE.CANDIDATE,
            handleViewCandidate,
            handleProgressOnboardingByIds,
            handleProgressRegistrationByIds,
            handleDeleteCandidateByIds,
            isBureau,
            isUK,
            isIreland,
          })}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
          rowSelection={{
            type: 'checkbox',
            onChange: (
              _selectedRowKeys: React.Key[],
              selectedRows: Candidate[],
            ) => {
              setSelectedRows(selectedRows);
            },
          }}
          noDataType={isFiltering ? 'noDataFound' : 'noDataCreated'}
          addNewBtn={renderAddNewButton('outlined')}
        />
      </AppsContent>
      <ViewCandidateModal
        isOpen={viewCandidateModal.open}
        toggle={toggleViewCandidateModal}
        data={viewCandidateModal.data}
        refetchData={refetchData}
        handleProgressOnboardingByIds={handleProgressOnboardingByIds}
        handleProgressRegistrationByIds={handleProgressRegistrationByIds}
        handleDeleteCandidateByIds={handleDeleteCandidateByIds}
      />
    </AppCard>
  );
};

export default CandidateTab;
